/**
 * Ticket Z14: Serverfunktionen der gemeinsamen Anfragenliste.
 *
 * Öffentlich: nur das Absenden einer Anfrage (Turnstile-geschützt, Ablage über
 * den Dienstschlüssel). Lesen und Statuspflege ausschließlich intern.
 */

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import {
  INQUIRY_SOURCES,
  INQUIRY_STATUSES,
  trainingInquirySchema,
  type InquirySource,
  type InquiryStatus,
  type InquiryStatusFilter,
} from "@/lib/inquiries";

export const submitTrainingInquiry = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => trainingInquirySchema.parse(input))
  .handler(async ({ data }) => {
    const { submitTrainingInquiryOnServer } = await import("@/lib/inquiries.server");
    return submitTrainingInquiryOnServer(data);
  });

const listSchema = z.object({
  source: z.enum(INQUIRY_SOURCES).nullable().optional(),
  status: z
    .enum(INQUIRY_STATUSES)
    .nullable()
    .optional()
    .or(z.literal("neu_und_in_bearbeitung")),
});

export const listInquiries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => listSchema.parse(input ?? {}))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { listInquiriesOnServer } = await import("@/lib/inquiries.server");
    return listInquiriesOnServer(context.supabase as never, {
      source: (data.source ?? null) as InquirySource | null,
      status: (data.status ?? null) as InquiryStatusFilter | null,
    });
  });

const updateSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(INQUIRY_STATUSES),
  internalNote: z.string().trim().max(2000).nullable().optional(),
});

export const updateInquiryStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { updateInquiryStatusOnServer } = await import("@/lib/inquiries.server");
    await updateInquiryStatusOnServer(
      context.supabase as never,
      data.id,
      data.status,
      data.internalNote ?? null,
    );
    return { ok: true as const };
  });
